import React, { useEffect, useState } from 'react'
import api from '../api'
import { RefreshCw, Search, Tag, Wifi, Smartphone, AlertTriangle, Inbox, CheckCircle2, XCircle, Stethoscope, Zap, Loader2, Eye, EyeOff } from 'lucide-react'
import { Card, PageHeader, Input, Select, Button, Badge, Dot, Modal, ModalHeader, Alert, Field, EmptyState, Pagination } from '../components/ui'
import DiagnostikModal, { Info, Section, rxPowerColor } from '../components/DiagnostikModal'

const PAGE_SIZE = 50
// Cross-link ke Daftar Pelanggan (2026-07-24) -- lihat komentar enrichDenganBilling di
// server/routes/genieacs.js. Sama persis STATUS_TONE di PelangganKontak.jsx.
const STATUS_TONE = { AKTIF: 'success', ISOLIR: 'warning', BERHENTI: 'danger' }

function maskPass(pass) {
  if (!pass || pass === 'N/A') return 'N/A'
  return '•'.repeat(Math.min(pass.length, 12))
}

export default function Perangkat() {
  const [data, setData] = useState([])
  const [stats, setStats] = useState({ total: 0, online: 0 })
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [registeredFilter, setRegisteredFilter] = useState('all')
  const [refreshingAll, setRefreshingAll] = useState(false)
  const [detail, setDetail] = useState(null)
  const [diagUsername, setDiagUsername] = useState(null)
  const [syncingUsername, setSyncingUsername] = useState(null)
  const [modemPending, setModemPending] = useState(0)
  const [modemChecking, setModemChecking] = useState(false)

  // Debounce: tunggu user berhenti mengetik dulu sebelum query ke server
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); setSearch(searchInput) }, 400)
    return () => clearTimeout(t)
  }, [searchInput])

  useEffect(() => { load() }, [page, search, statusFilter, registeredFilter])
  useEffect(() => { loadModemPending() }, [])

  async function loadModemPending() {
    try {
      const res = await api.get('/genieacs/modem-check/status')
      setModemPending(res.data.pending)
    } catch { /* bukan fitur kritikal, diamkan kalau gagal */ }
  }

  // Cek jenis modem HANYA utk pelanggan yang belum terdaftar GenieACS & belum pernah teridentifikasi —
  // backend yang menentukan daftarnya (lihat GET /modem-check/status), di sini cuma trigger + tunggu.
  async function runModemCheck() {
    setModemChecking(true)
    try {
      const res = await api.post('/genieacs/modem-check/run')
      alert(res.data.message)
      await Promise.all([load(), loadModemPending()])
    } catch (e) {
      alert(e.response?.data?.message || 'Gagal menjalankan pengecekan jenis modem')
    } finally { setModemChecking(false) }
  }

  async function load() {
    setLoading(true); setError('')
    try {
      const res = await api.get('/genieacs/devices', { params: {
        page, limit: PAGE_SIZE, search: search || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        registered: registeredFilter !== 'all' ? registeredFilter : undefined
      } })
      setData(res.data.items)
      setTotal(res.data.total)
      setStats(res.data.stats)
    } catch (e) {
      setError(e.response?.data?.message || 'Gagal memuat data perangkat dari GenieACS')
    } finally { setLoading(false) }
  }

  async function refreshAll() {
    setRefreshingAll(true)
    try {
      const res = await api.post('/genieacs/refresh-all')
      alert(res.data.message)
    } catch (e) {
      alert(e.response?.data?.message || 'Gagal refresh semua device')
    } finally { setRefreshingAll(false) }
  }

  async function syncGenieacs(username) {
    setSyncingUsername(username)
    try {
      const res = await api.post('/scripts/sync-genieacs/run', { username })
      alert(`${username}: ${res.data.message}`)
      if (res.data.success) load()
    } catch (e) {
      alert(`${username}: ${e.response?.data?.message || 'Gagal menjalankan sync'}`)
    } finally { setSyncingUsername(null) }
  }

  async function bukaDetail(id) {
    setDetail({ loading: true, _id: id })
    try {
      const res = await api.get(`/genieacs/devices/${encodeURIComponent(id)}`)
      setDetail({ ...res.data, loading: false, wifiForm: { ssid2G: '', ssid5G: '', password2G: '', password5G: '' }, tagForm: (res.data.tags || [])[0] || '' })
    } catch (e) {
      setDetail(null)
      alert(e.response?.data?.message || 'Gagal memuat detail device')
    }
  }

  return (
    <div>
      <PageHeader
        title="Perangkat Pelanggan"
        subtitle={<>Gabungan Mikrotik + GenieACS &middot; {stats.total} akun &middot; <span style={{ color: 'var(--success)', fontWeight: 600 }}>{stats.online} online</span> &middot; <span style={{ color: 'var(--danger)', fontWeight: 600 }}>{stats.total - stats.online} offline</span> &middot; <span style={{ color: 'var(--warning)', fontWeight: 600 }}>{stats.unregistered} belum terdaftar GenieACS</span></>}
        actions={<>
          <Select value={statusFilter} onChange={e => { setPage(1); setStatusFilter(e.target.value) }} style={{ width: 140 }}>
            <option value="all">Semua Status</option>
            <option value="online">Online</option>
            <option value="offline">Offline</option>
          </Select>
          <Select value={registeredFilter} onChange={e => { setPage(1); setRegisteredFilter(e.target.value) }} style={{ width: 170 }}>
            <option value="all">Semua GenieACS</option>
            <option value="yes">Terdaftar</option>
            <option value="no">Belum Terdaftar</option>
          </Select>
          <Input value={searchInput} onChange={e => setSearchInput(e.target.value)}
            placeholder="Cari username, IP, SSID..." style={{ width: 240 }} />
          <Button icon={Search} variant="soft" loading={modemChecking}
            disabled={!modemChecking && modemPending === 0}
            title={modemPending === 0 ? 'Semua modem pelanggan sudah teridentifikasi' : `${modemPending} pelanggan belum diketahui jenis modemnya`}
            onClick={runModemCheck}>
            Cek Jenis Modem{modemPending > 0 ? ` (${modemPending})` : ''}
          </Button>
          <Button icon={RefreshCw} loading={refreshingAll} onClick={refreshAll}>Refresh Semua</Button>
        </>}
      />

      {error && <Alert type="error">{error}</Alert>}

      <p style={{ fontSize: 12, color: 'var(--text-faint)', margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: 16 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><CheckCircle2 size={13} color="var(--success)" /> = terdaftar di GenieACS</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><XCircle size={13} color="var(--warning)" /> = belum terdaftar di GenieACS</span>
      </p>

      <Card style={{ overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#fafafc' }}>
              {['', 'Status', 'PPPoE Username', 'Pelanggan', 'Jenis Modem', 'IP PPPoE', 'Redaman', 'SSID 2.4G', 'Terhubung', ''].map(h => (
                <th key={h} style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.3, whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} style={{ padding: 48, textAlign: 'center', color: 'var(--text-faint)' }}>Memuat...</td></tr>
            ) : data.length === 0 ? (
              <tr><td colSpan={10}><EmptyState icon={Inbox} title="Tidak ada perangkat" /></td></tr>
            ) : data.map(d => (
              <tr key={d.pppUsername} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={{ padding: '10px 16px' }}><Dot on={d.online} /></td>
                <td style={{ padding: '10px 16px' }}>
                  {d.registered ? (
                    <span title="Sudah terdaftar di GenieACS" style={{ display: 'inline-flex' }}><CheckCircle2 size={16} color="var(--success)" /></span>
                  ) : (
                    <span title="Belum terdaftar di GenieACS" style={{ display: 'inline-flex' }}><XCircle size={16} color="var(--warning)" /></span>
                  )}
                </td>
                <td style={{ padding: '10px 16px', whiteSpace: 'nowrap' }}>{d.pppUsername}</td>
                <td style={{ padding: '10px 16px', whiteSpace: 'nowrap' }}>
                  {d.namaPelanggan ? (
                    <>
                      <div style={{ fontWeight: 600 }}>{d.namaPelanggan}</div>
                      {d.statusBilling && <Badge tone={STATUS_TONE[d.statusBilling] || 'neutral'}>{d.statusBilling}</Badge>}
                    </>
                  ) : <span style={{ color: 'var(--text-faint)' }}>Belum di Daftar Pelanggan</span>}
                </td>
                <td style={{ padding: '10px 16px', whiteSpace: 'nowrap', color: d.model === 'Tidak terdeteksi' ? 'var(--danger)' : d.model === 'N/A' ? 'var(--text-faint)' : 'var(--text)' }}>
                  {d.model === 'N/A' ? '-' : d.model}
                </td>
                <td style={{ padding: '10px 16px', whiteSpace: 'nowrap' }}>{d.pppoeIP}</td>
                <td style={{ padding: '10px 16px', fontWeight: 600, color: rxPowerColor(d.rxPower), whiteSpace: 'nowrap' }}>{d.rxPower !== 'N/A' ? `${d.rxPower} dBm` : 'N/A'}</td>
                <td style={{ padding: '10px 16px', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.ssid2G}</td>
                <td style={{ padding: '10px 16px', textAlign: 'center' }}>{d.connectedCount ?? '-'}</td>
                <td style={{ padding: '10px 16px', display: 'flex', gap: 6 }}>
                  {d.registered ? (
                    <Button size="sm" variant="soft" onClick={() => bukaDetail(d._id)}>Detail</Button>
                  ) : (
                    <Button size="sm" variant="soft" icon={Zap} title="Sinkron ke GenieACS"
                      loading={syncingUsername === d.pppUsername}
                      disabled={syncingUsername && syncingUsername !== d.pppUsername}
                      onClick={() => syncGenieacs(d.pppUsername)}>Sync</Button>
                  )}
                  <Button size="sm" variant="ghost" icon={Stethoscope} title="Diagnostik"
                    onClick={() => setDiagUsername(d.pppUsername)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination page={page} limit={PAGE_SIZE} total={total} onChange={setPage} />
      </Card>

      {detail && <DetailModal detail={detail} onClose={() => setDetail(null)} onSaved={load} setDetail={setDetail} />}
      {diagUsername && <DiagnostikModal username={diagUsername} onClose={() => setDiagUsername(null)} />}
      {modemChecking && (
        <Modal width={420}>
          <div style={{ padding: '16px 4px 8px', textAlign: 'center' }}>
            <Loader2 className="spin" size={32} color="var(--brand)" style={{ marginBottom: 14 }} />
            <p style={{ fontWeight: 600, marginBottom: 4 }}>Mengecek jenis modem...</p>
            <p style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
              Memeriksa satu per satu perangkat pelanggan yang belum diketahui jenis modemnya. Proses berjalan di background — jangan tutup atau refresh halaman ini.
            </p>
          </div>
        </Modal>
      )}
    </div>
  )
}

function DetailModal({ detail, onClose, onSaved, setDetail }) {
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState(null)
  const [showPass, setShowPass] = useState(false)

  if (detail.loading) {
    return (
      <Modal onClose={onClose} width={560}>
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-faint)' }}>Memuat detail...</div>
      </Modal>
    )
  }

  async function aksi(fn, successMsg) {
    setBusy(true); setMsg(null)
    try {
      await fn()
      setMsg({ type: 'success', text: successMsg })
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.message || 'Gagal' })
    } finally { setBusy(false) }
  }

  async function simpanWifi() {
    const f = detail.wifiForm
    const payload = {}
    if (f.ssid2G) payload.ssid2G = f.ssid2G
    if (f.ssid5G) payload.ssid5G = f.ssid5G
    if (f.password2G) payload.password2G = f.password2G
    if (f.password5G) payload.password5G = f.password5G
    if (!Object.keys(payload).length) { setMsg({ type: 'error', text: 'Isi minimal satu field' }); return }
    await aksi(
      () => api.post(`/genieacs/devices/${encodeURIComponent(detail._id)}/wifi`, payload),
      'Pengaturan WiFi terkirim ke perangkat'
    )
  }

  async function simpanTag() {
    await aksi(
      () => api.post(`/genieacs/devices/${encodeURIComponent(detail._id)}/tag`, { customerNumber: detail.tagForm }),
      'Nomor pelanggan berhasil diupdate'
    )
    onSaved()
  }

  return (
    <Modal onClose={onClose} width={560}>
      <ModalHeader title={detail.model} subtitle={detail._id} onClose={onClose} />

      {msg && <Alert type={msg.type}>{msg.text}</Alert>}

      {/* Info ringkas */}
      <div className="grid-2" style={{ gap: 10, marginBottom: 22, fontSize: 13 }}>
        {detail.namaPelanggan && <Info label="Nama Pelanggan (Billing)" value={detail.namaPelanggan} />}
        {detail.statusBilling && <Info label="Status Billing" value={detail.statusBilling} color={detail.statusBilling === 'AKTIF' ? 'var(--success)' : detail.statusBilling === 'ISOLIR' ? 'var(--warning)' : 'var(--danger)'} />}
        {detail.namaPaketBilling && <Info label="Paket Langganan" value={detail.namaPaketBilling} />}
        <Info label="Status" value={detail.online ? 'Online' : 'Offline'} color={detail.online ? 'var(--success)' : 'var(--danger)'} />
        <Info label="Redaman (RX Power)" value={detail.rxPower !== 'N/A' ? `${detail.rxPower} dBm` : 'N/A'} color={rxPowerColor(detail.rxPower)} />
        <Info label="Serial Number" value={detail.serialNumber} />
        <Info label="Uptime" value={detail.uptime} />
        <Info label="PPPoE Username" value={detail.pppUsername} />
        <Info label="IP PPPoE" value={detail.pppoeIP} />
        <Info label="IP TR-069" value={detail.tr069IP} />
        <Info label="MAC" value={detail.mac} />
        <Info label="SSID 2.4G" value={detail.ssid2G} />
        <Info label="SSID 5G" value={detail.ssid5G} />
        <Info label="Password WiFi 2.4G" value={showPass ? (detail.wifiPassword2G || 'N/A') : maskPass(detail.wifiPassword2G)} />
        <Info label="Password WiFi 5G" value={showPass ? (detail.wifiPassword5G || 'N/A') : maskPass(detail.wifiPassword5G)} />
      </div>
      <button onClick={() => setShowPass(s => !s)} style={{
        display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-muted)',
        background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginTop: -12, marginBottom: 22
      }}>
        {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
        {showPass ? 'Sembunyikan password WiFi' : 'Tampilkan password WiFi'}
      </button>

      {/* Nomor pelanggan */}
      <Section title="Nomor Pelanggan" icon={Tag}>
        <div style={{ display: 'flex', gap: 10 }}>
          <Input value={detail.tagForm} onChange={e => setDetail({ ...detail, tagForm: e.target.value })}
            placeholder="0812xxxxxxx" style={{ flex: 1 }} />
          <Button disabled={busy} onClick={simpanTag}>Simpan</Button>
        </div>
      </Section>

      {/* WiFi */}
      <Section title="Ubah SSID / Password WiFi" icon={Wifi}>
        <div className="grid-2" style={{ gap: 10 }}>
          {[
            { key: 'ssid2G', label: 'SSID 2.4G', placeholder: detail.ssid2G },
            { key: 'ssid5G', label: 'SSID 5G', placeholder: detail.ssid5G },
            { key: 'password2G', label: 'Password 2.4G (min 8 char)', placeholder: detail.wifiPassword2G !== 'N/A' ? (showPass ? detail.wifiPassword2G : maskPass(detail.wifiPassword2G)) : undefined },
            { key: 'password5G', label: 'Password 5G (min 8 char)', placeholder: detail.wifiPassword5G !== 'N/A' ? (showPass ? detail.wifiPassword5G : maskPass(detail.wifiPassword5G)) : undefined },
          ].map(f => (
            <Field key={f.key} label={f.label}>
              <Input value={detail.wifiForm[f.key]} placeholder={f.placeholder}
                onChange={e => setDetail({ ...detail, wifiForm: { ...detail.wifiForm, [f.key]: e.target.value } })} />
            </Field>
          ))}
        </div>
        <Button disabled={busy} onClick={simpanWifi} style={{ marginTop: 12 }}>Kirim ke Perangkat</Button>
      </Section>

      {/* Perangkat terhubung */}
      <Section title={`Perangkat Terhubung (${detail.connectedHosts.length})`} icon={Smartphone}>
        {detail.connectedHosts.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Tidak ada data perangkat terhubung (device mungkin belum kirim data host terbaru — coba Refresh).</p>
        ) : (
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Nama', 'IP', 'MAC', 'Interface'].map(h => (
                  <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {detail.connectedHosts.map((h, i) => (
                <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '7px 10px' }}>{h.hostName}</td>
                  <td style={{ padding: '7px 10px' }}>{h.ipAddress}</td>
                  <td style={{ padding: '7px 10px' }}>{h.macAddress}</td>
                  <td style={{ padding: '7px 10px' }}>{h.interfaceType}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {/* Aksi */}
      <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
        <Button disabled={busy} variant="soft" icon={RefreshCw}
          onClick={() => aksi(() => api.post(`/genieacs/devices/${encodeURIComponent(detail._id)}/refresh`), 'Perintah refresh terkirim')}
          style={{ flex: 1, justifyContent: 'center' }}>Refresh Device</Button>
        <Button disabled={busy} variant="danger-soft" icon={AlertTriangle}
          onClick={() => {
            if (!window.confirm('Reboot perangkat ini sekarang? Koneksi pelanggan akan terputus sesaat.')) return
            aksi(() => api.post(`/genieacs/devices/${encodeURIComponent(detail._id)}/reboot`), 'Perintah reboot terkirim')
          }} style={{ flex: 1, justifyContent: 'center' }}>Reboot Device</Button>
      </div>
    </Modal>
  )
}
